import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material'
import PointOfSaleIcon from '@mui/icons-material/PointOfSale'
import EventIcon from '@mui/icons-material/CalendarMonth'
import EditIcon from '@mui/icons-material/EditOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import BadgeIcon from '@mui/icons-material/BadgeOutlined'
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined'
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined'
import { useCustomer, useRendezVousDuClient, useVentesDuClient } from '../data/hooks.js'
import {
  dateHeureLisible,
  estEngagement,
  format,
  LIBELLES_STATUT_RDV,
  money,
  referenceAffichable,
  resteDu,
  signeDansLeChiffre,
  type CurrencyCode,
} from '@iobiz/shared'
import { CustomerFormDialog } from '../features/customers/CustomerFormDialog.js'
import { VignetteFiche } from '../ui/VignetteFiche.js'
import { ConfirmDialog } from '../ui/ConfirmDialog.js'
import { removeCustomer } from '../data/repositories/customers.write.js'
import { useSessionStore } from '../stores/sessionStore.js'
import { api } from '../api/client.js'
import { QrCode } from '../ui/QrCode.js'
import { CarteFidelite } from '../features/customers/CarteFidelite.js'

/**
 * Fiche client (W-09) — charpente « 360° » réutilisée ensuite pour les
 * articles et les fournisseurs : entête, onglets, panneau latéral. Une seule
 * structure à construire, un seul modèle mental pour l'utilisateur (§A2).
 */

/**
 * « 12/03/1988 (37 ans) ».
 *
 * ⭐ L'âge est calculé, jamais stocké : une donnée dérivée qu'on enregistre
 * devient fausse le lendemain. Il est aussi ce qu'on cherche vraiment en
 * ouvrant la fiche — la date seule oblige à faire le calcul de tête.
 *
 * ⚠️ Découpage TEXTUEL de la date, sans passer par `new Date` : construire un
 * objet depuis « 1988-03-12 » le place à minuit UTC, que l'affichage local
 * ramène à la veille sous les fuseaux négatifs. Le client naîtrait le 11.
 */
function naissanceLisible(iso: string | null): string | null {
  if (!iso) return null
  const [annee, mois, jour] = iso.split('-').map(Number)
  if (!annee || !mois || !jour) return null

  const today = new Date()
  let age = today.getFullYear() - annee
  // L'anniversaire de cette année est-il déjà passé ?
  const passe = today.getMonth() + 1 > mois || (today.getMonth() + 1 === mois && today.getDate() >= jour)
  if (!passe) age -= 1

  const date = `${String(jour).padStart(2, '0')}/${String(mois).padStart(2, '0')}/${annee}`
  return age >= 0 ? `${date} (${age} ans)` : date
}

/** Libellés des types de document — les mêmes que sur la fiche d'une vente. */
const TYPES_DOC: Record<string, string> = {
  quote: 'Devis',
  invoice: 'Vente',
  deposit_invoice: 'Acompte',
  credit_note: 'Avoir',
  order: 'Commande',
}

function Field({ label, value }: { label: string; value: string | null }): JSX.Element {
  return (
    <Box sx={{ display: 'flex', py: 2, borderBottom: 1, borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', width: 180, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value?.trim() ? value : '—'}</Typography>
    </Box>
  )
}

export function CustomerDetailPage(): JSX.Element {
  const { slug, id } = useParams<{ slug: string; id: string }>()
  const navigate = useNavigate()
  const store = useSessionStore((s) => (slug ? s.storeBySlug(slug) : undefined))
  const ventes = useVentesDuClient(store?.storeId, id)
  const rdvs = useRendezVousDuClient(store?.storeId, id)
  /**
   * Impact de la suppression, compté par le SERVEUR : la réplique locale peut
   * ne pas détenir tous les documents anciens, et annoncer un chiffre trop bas
   * serait pire que de n'en annoncer aucun.
   */
  const [impact, setImpact] = useState<{ brouillons: number; valides: number } | null>(null)
  const brouillons = impact?.brouillons ?? 0
  /**
   * ⚠️ Construit depuis l'ORIGINE courante : le même code doit fonctionner en
   * local, sur dev et en production sans qu'aucune adresse ne soit écrite en
   * dur — un QR figé sur le mauvais domaine ne se voit qu'une fois imprimé.
   */
  const lienFiche = `${window.location.origin}/s/${slug}/clients/${id}`
  /** Documents ENGAGEANTS : un devis, même numéroté, n'en est pas un. */
  const documents = (ventes ?? []).filter((v) => v.status !== 'draft' && v.docType !== 'quote')
  const valides = impact?.valides ?? documents.length
  const deviseClient = (store?.currencyCode ?? 'XAF') as CurrencyCode
  const prixClient = (v: number): string => format(money(v, deviseClient), 'fr-FR')
  /**
   * Ce que ce client a réellement acheté.
   *
   * ⚠️ La règle vient du paquet partagé : un devis numéroté porte le statut
   * « validé » et passerait un filtre écrit à la main sur le seul statut.
   */
  const chiffre = (ventes ?? []).reduce(
    (somme, v) => somme + signeDansLeChiffre(v) * v.totalAmount,
    0,
  )

  // ⭐ Lue depuis la RÉPLIQUE : la fiche reste consultable hors ligne, y
  // compris celle qu'on vient de créer sans réseau.
  const customer = useCustomer(store?.storeId, id)
  const loading = customer === undefined
  const [tab, setTab] = useState(0)
  const [editing, setEditing] = useState(false)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [carteOuverte, setCarteOuverte] = useState(false)

  if (loading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 300 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  if (!customer) {
    return (
      <Alert severity="error" action={<Button onClick={() => navigate(-1)}>Retour</Button>}>
        Client introuvable dans les données de cet appareil.
      </Alert>
    )
  }

  const address = [
    customer.addressLine1,
    customer.addressLine2,
    [customer.postalCode, customer.city].filter(Boolean).join(' '),
    customer.countryCode,
  ]
    .filter((v) => v?.trim())
    .join(', ')

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 4 }}>
        <Link
          component="button"
          variant="body2"
          underline="hover"
          onClick={() => navigate(`/s/${slug}/clients`)}
        >
          Clients
        </Link>
        <Typography variant="body2" color="text.primary">
          {customer.displayName}
        </Typography>
      </Breadcrumbs>

      <Paper sx={{ p: 6, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Vignette de référence de l'application : c'est cette fiche qui
              donne le ton, les deux autres s'y alignent. */}
          <VignetteFiche
            images={customer.avatarUrl ? [customer.avatarUrl] : []}
            repli={
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: 'text.disabled' }}>
                {customer.displayName.slice(0, 2).toUpperCase()}
              </Typography>
            }
          />
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="h1">{customer.displayName}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {customer.type === 'company' ? 'Société' : 'Particulier'}
              {customer.code && ` · ${customer.code}`}
              {' · Client depuis '}
              {new Date(customer.createdAt).toLocaleDateString('fr-FR', {
                month: 'long',
                year: 'numeric',
              })}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
              {customer.tags.map((t) => (
                <Chip key={t} label={t} size="small" />
              ))}
            </Box>
          </Box>
          {/* Actions rapides (§A3) : lancer une vente ou un RDV sans quitter la fiche. */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
              Modifier
            </Button>
            {/* ⚠️ Le bouton « Vendre » a disparu : il était DÉSACTIVÉ depuis le
                premier sprint, et l'action vit maintenant dans le menu, à côté
                de « Nouveau devis ». Deux commandes pour le même geste, dont
                une inerte, apprennent à ne plus lire la barre. */}
            <Button variant="outlined" startIcon={<EventIcon />} disabled>
              Rendez-vous
            </Button>
            {/* La suppression vit dans un menu, pas en façade : une action
                destructrice ne se place pas sous le pouce, à côté de « Modifier ». */}
            <IconButton
              aria-label="Autres actions"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2 }}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
            >
              {/* ⭐ La carte porte la photo ET le code QR : la photo pour
                  vérifier que le porteur est bien le titulaire, le QR pour
                  ouvrir sa fiche d'un scan au comptoir. */}
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  setCarteOuverte(true)
                }}
              >
                <BadgeIcon sx={{ fontSize: 18, mr: 2 }} />
                Carte de fidélité
              </MenuItem>
              {/* ⭐ Les deux documents partent du MÊME écran, l'un en mode
                  vente et l'autre en mode devis : même recherche, même
                  douchette, mêmes vues. Le client est déjà rempli — c'est tout
                  l'intérêt de partir de sa fiche. */}
              <MenuItem onClick={() => navigate(`/s/${slug}/caisse?client=${customer.id}`)}>
                <PointOfSaleIcon sx={{ fontSize: 18, mr: 2 }} />
                Nouvelle vente
              </MenuItem>
              <MenuItem onClick={() => navigate(`/s/${slug}/devis?client=${customer.id}`)}>
                <DescriptionIcon sx={{ fontSize: 18, mr: 2 }} />
                Nouveau devis
              </MenuItem>
              {/* La commande est le troisième mode du même écran : un
                  engagement ferme, qui n'a ni sorti de stock ni produit de
                  recette tant qu'il n'est pas facturé. */}
              <MenuItem onClick={() => navigate(`/s/${slug}/commande?client=${customer.id}`)}>
                <AssignmentIcon sx={{ fontSize: 18, mr: 2 }} />
                Nouvelle commande
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null)
                  setConfirming(true)
                  // Le compte est demandé À L'OUVERTURE : afficher une
                  // confirmation puis la corriger sous les yeux de
                  // l'utilisateur serait pire que de ne rien annoncer.
                  if (store && id) {
                    void api
                      .get<{ brouillons: number; valides: number }>(
                        `/stores/${store.storeId}/customers/${id}/impact`,
                      )
                      .then(setImpact)
                      .catch(() => setImpact(null))
                  }
                }}
                sx={{ color: 'error.main' }}
              >
                <DeleteIcon sx={{ fontSize: 18, mr: 2 }} />
                Supprimer
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </Paper>

      <Tabs value={tab} onChange={(_, v: number) => setTab(v)} sx={{ mb: 4 }}>
        <Tab label="Informations" />
        <Tab label={`Transactions${ventes && ventes.length > 0 ? ` ${ventes.length}` : ''}`} />
        <Tab label={`Rendez-vous${rdvs && rdvs.length > 0 ? ` ${rdvs.length}` : ''}`} />
        <Tab label="Documents" disabled />
        <Tab label="Tâches" disabled />
      </Tabs>

      {tab === 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 4,
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            alignItems: 'start',
          }}
        >
          <Paper sx={{ p: 6, borderRadius: 3 }}>
            <Typography variant="h3" sx={{ mb: 4 }}>
              Identité
            </Typography>
            <Field label="Référence" value={customer.code} />
            <Field label="Type" value={customer.type === 'company' ? 'Société' : 'Particulier'} />
            <Field label="Nom" value={customer.lastName} />
            <Field label="Prénom" value={customer.firstName} />
            <Field label="Société" value={customer.companyName} />
            <Field label="N° fiscal" value={customer.taxId} />
            <Field label="Date de naissance" value={naissanceLisible(customer.birthDate)} />

            <Typography variant="h3" sx={{ mb: 4, mt: 6 }}>
              Contact
            </Typography>
            <Field label="Téléphone" value={customer.phone} />
            <Field label="Mobile" value={customer.mobile} />
            <Field label="Email" value={customer.email} />
            <Field label="Adresse" value={address || null} />

            {/* ⭐ Le bloc n'apparaît que pour une SOCIÉTÉ, et seulement si l'on
                sait qui appeler : afficher « Personne à contacter » suivi de
                quatre tirets sur chaque fiche société apprend à sauter la
                section — y compris le jour où elle est remplie. */}
            {customer.type === 'company' &&
              [
                customer.contactLastName,
                customer.contactFirstName,
                customer.contactRole,
                customer.contactPhone,
                customer.contactEmail,
              ].some(Boolean) && (
                <>
                  <Typography variant="h3" sx={{ mb: 4, mt: 6 }}>
                    Personne à contacter
                  </Typography>
                  <Field
                    label="Nom"
                    value={
                      [customer.contactFirstName, customer.contactLastName]
                        .filter(Boolean)
                        .join(' ') || null
                    }
                  />
                  <Field label="Fonction" value={customer.contactRole} />
                  <Field label="Téléphone" value={customer.contactPhone} />
                  <Field label="Email" value={customer.contactEmail} />
                </>
              )}

            <Typography variant="h3" sx={{ mb: 4, mt: 6 }}>
              Commercial
            </Typography>
            <Field
              label="Conditions de paiement"
              value={
                customer.paymentTermsDays > 0 ? `${customer.paymentTermsDays} jours` : 'Comptant'
              }
            />
            <Field label="Notes" value={customer.notes} />
          </Paper>

          <Paper sx={{ p: 6, borderRadius: 3 }}>
            <Typography variant="h3" sx={{ mb: 4 }}>
              Résumé
            </Typography>
            <Field
              label="Dernière activité"
              // Date ET heure : un client peut passer deux fois dans la
              // journée, et « aujourd'hui » ne dit pas lequel des deux passages.
              value={
                customer.lastActivityAt ? dateHeureLisible(customer.lastActivityAt) : 'Aucune'
              }
            />
            <Field
              label="Créé le"
              value={new Date(customer.createdAt).toLocaleDateString('fr-FR')}
            />
            <Field label="Version" value={String(customer.version)} />
            <Field label="Chiffre d’affaires" value={prixClient(chiffre)} />
            <Field
              label="Dernier achat"
              value={
                ventes && ventes[0]
                  ? new Date(ventes[0].issueDate).toLocaleDateString('fr-FR')
                  : null
              }
            />

            {/* ⭐ Le QR pointe vers CETTE fiche. Scanné depuis un téléphone au
                comptoir, ou collé sur une carte de fidélité, il ramène au
                dossier du client sans le chercher dans la liste — c'est le
                geste qu'on répète le plus souvent devant quelqu'un qui attend. */}
            <Box sx={{ mt: 5, display: 'flex', gap: 4, alignItems: 'center' }}>
              <QrCode valeur={lienFiche} titre={`Fiche de ${customer.displayName}`} />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Accès rapide
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Scannez pour ouvrir cette fiche. Imprimable sur une carte de fidélité.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              gap: 3,
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Ventes et devis rattachés à ce client.
            </Typography>
            {/* ⭐ Le devis se rédige DEPUIS la fiche : c'est là qu'on est quand
                un client demande un prix, et l'y renvoyer depuis la caisse
                aurait fait perdre le contexte. */}
            <Button
              variant="outlined"
              startIcon={<DescriptionIcon />}
              onClick={() => navigate(`/s/${slug}/devis?client=${customer.id}`)}
            >
              Nouveau devis
            </Button>
          </Box>

          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {!ventes || ventes.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  Aucune vente ni devis pour ce client.
                </Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Référence</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Date et heure</TableCell>
                      {/* ⚠️ « Encaissé par » ne convient plus : ce tableau
                          mélange ventes et devis, et un devis ne s'encaisse
                          pas. « Établi par » dit vrai des deux. */}
                      <TableCell>Établi par</TableCell>
                      <TableCell align="right">Reste dû</TableCell>
                      <TableCell align="right">Total TTC</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ventes.map((v) => (
                      <TableRow
                        key={v.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/s/${slug}/ventes/${v.id}`)}
                      >
                        <TableCell>
                          {/* ⭐ Un vrai LIEN, pas seulement une ligne cliquable :
                              rien n'annonçait qu'on pouvait ouvrir la vente, et
                              le clavier n'y avait pas accès. */}
                          <Link
                            component="button"
                            variant="body2"
                            underline="hover"
                            sx={{ fontWeight: 600 }}
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate(`/s/${slug}/ventes/${v.id}`)
                            }}
                          >
                            {referenceAffichable(v)}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={v.docType === 'quote' ? 'Devis' : TYPES_DOC[v.docType]}
                            color={v.docType === 'quote' ? 'info' : 'default'}
                          />
                        </TableCell>
                        <TableCell>{dateHeureLisible(v.createdAt)}</TableCell>
                        <TableCell>{v.cashierName ?? '—'}</TableCell>
                        <TableCell align="right" className="tabular">
                          {/* ⚠️ Rien n'est dû sur un DEVIS ni une COMMANDE : le
                              filtre ne portait que sur le statut, et les deux,
                              numérotés, sont « validés » — leur ligne
                              réclamait le total entier au client. */}
                          {!estEngagement(v.docType) && v.status !== 'draft' && resteDu(v) > 0
                            ? prixClient(resteDu(v))
                            : '—'}
                        </TableCell>
                        <TableCell align="right" className="tabular">
                          {prixClient(v.totalAmount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>
        </Box>
      )}

      {/* ─────────────────────────────────────────── onglet rendez-vous */}
      {tab === 2 && (
        <Box>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Tous les rendez-vous de ce client, du plus récent au plus ancien.
          </Typography>

          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            {!rdvs || rdvs.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography sx={{ color: 'text.secondary' }}>
                  Aucun rendez-vous pour ce client.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Quand</TableCell>
                    <TableCell>Prestation</TableCell>
                    <TableCell>Document</TableCell>
                    <TableCell>État</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rdvs.map((r) => {
                    // ⭐ Passé ou à venir : la distinction est ce qu'on lit en
                    // premier sur une fiche client — « ce qu'on lui a fait »
                    // n'est pas « ce qu'on lui a promis ».
                    const passe = r.endsAt < new Date().toISOString()
                    return (
                      <TableRow
                        key={r.id}
                        hover
                        sx={{ cursor: 'pointer', opacity: r.status === 'cancelled' ? 0.55 : 1 }}
                        onClick={() =>
                          // ⚠️ Vers l'ARTICLE, c'est-à-dire la prestation
                          // concernée. Le document, lui, a sa propre colonne :
                          // les deux réponses sont utiles et ne se remplacent
                          // pas.
                          r.itemId
                            ? navigate(`/s/${slug}/catalogue/${r.itemId}`)
                            : navigate(`/s/${slug}/agenda?jour=${r.startsAt.slice(0, 10)}`)
                        }
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {new Date(r.startsAt).toLocaleString('fr-FR', {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {passe ? 'passé' : 'à venir'}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.label}</TableCell>
                        <TableCell>
                          {r.transactionRef ? (
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'primary.main',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/s/${slug}/ventes/${r.transactionId}`)
                              }}
                            >
                              {r.transactionRef}
                            </Typography>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={LIBELLES_STATUT_RDV[r.status]}
                            color={r.status === 'cancelled' ? 'default' : 'primary'}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </Paper>
        </Box>
      )}

      {store && (
        <CustomerFormDialog
          open={editing}
          storeId={store.storeId}
          customer={customer}
          onClose={() => setEditing(false)}
          // La réponse du serveur remplace l'état local : elle porte la
          // nouvelle version, indispensable au prochain If-Match.
          onSaved={() => undefined}
        />
      )}

      {store && (
        <CarteFidelite
          open={carteOuverte}
          customer={customer}
          boutique={store}
          onClose={() => setCarteOuverte(false)}
        />
      )}

      <ConfirmDialog
        open={confirming}
        title="Supprimer ce client ?"
        /**
         * ⭐ Le message ÉNUMÈRE ce qui part et ce qui reste, avec des nombres.
         * « Supprimer ce client ? » ne permet pas de décider ; savoir que neuf
         * factures sont en jeu, si.
         */
        message={
          `${customer.displayName} n’apparaîtra plus dans les listes ni dans la recherche.` +
          (brouillons > 0
            ? ` ${brouillons} brouillon${brouillons > 1 ? 's' : ''} rattaché${brouillons > 1 ? 's' : ''} ${brouillons > 1 ? 'seront supprimés' : 'sera supprimé'} avec lui.`
            : '') +
          (valides > 0
            ? ` ⚠️ ${valides} document${valides > 1 ? 's' : ''} validé${valides > 1 ? 's' : ''} ${valides > 1 ? 'sont conservés' : 'est conservé'} : une facture porte un numéro légal dans une suite continue, et l’effacer ouvrirait un trou irréparable. Le nom du client y reste inscrit.`
            : '')
        }
        onConfirm={async () => {
          // Retirée de la réplique et mise en file : la suppression aboutit
          // même hors ligne, et partira au retour du réseau.
          await removeCustomer(store!.storeId, customer)
          navigate(`/s/${slug}/clients`)
        }}
        onClose={() => setConfirming(false)}
      />
    </Box>
  )
}
